import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { toast } from "react-toastify";
import api from "utils/api";

function EditProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    productName: "",
    priceNormal: "",
    discountPrice: "",
  });
  const [imageBanner, setImageBanner] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    fetchProductData();
  }, [id]);

  const fetchProductData = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      if (response.data.success) {
        const product = response.data.data;
        setFormData({
          productName: product.productName || "",
          priceNormal: product.priceNormal || "",
          discountPrice: product.discountPrice || "",
        });
        setExistingImage(product.imageBanner || "");
        setImagePreview(product.imageBanner || "");
      } else {
        toast.error("Failed to fetch product details");
        navigate("/product");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error(error.response?.data?.message || "Failed to fetch product details");
      navigate("/product");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setImageBanner(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.productName.trim()) {
      toast.error("Please enter product name");
      return;
    }
    if (!formData.priceNormal || formData.priceNormal <= 0) {
      toast.error("Please enter valid normal price");
      return;
    }
    if (formData.discountPrice === "" || parseFloat(formData.discountPrice) < 0) {
      toast.error("Please enter valid discount price");
      return;
    }
    if (parseFloat(formData.discountPrice) > parseFloat(formData.priceNormal)) {
      toast.error("Discount price cannot be greater than normal price");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append("productName", formData.productName);
      data.append("priceNormal", formData.priceNormal);
      data.append("discountPrice", formData.discountPrice);

      // Only append new image if selected
      if (imageBanner) {
        data.append("imageBanner", imageBanner);
      }

      const response = await api.put(`/products/${id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Product updated successfully");
        navigate("/product");
      } else {
        toast.error(response.data.message || "Failed to update product");
      }
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error(error.response?.data?.message || "Failed to update product");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonTypography variant="h5" fontWeight="medium">
                Edit Product
              </ArgonTypography>
              <ArgonButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => navigate("/product")}
              >
                Back to List
              </ArgonButton>
            </ArgonBox>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Product Image Upload */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Product Image
                  </ArgonTypography>
                  <ArgonBox>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{
                        display: "block",
                        padding: "10px",
                        border: "1px solid #d2d6da",
                        borderRadius: "8px",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    />
                  </ArgonBox>
                  {imagePreview && (
                    <ArgonBox mt={2}>
                      <ArgonTypography variant="caption" color="text" mb={1}>
                        {imageBanner ? "New Image Preview:" : "Current Image:"}
                      </ArgonTypography>
                      <ArgonBox>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: "300px",
                            maxHeight: "200px",
                            borderRadius: "8px",
                            border: "1px solid #d2d6da",
                          }}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  )}
                </Grid>

                {/* Product Name */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Product Name *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    name="productName"
                    value={formData.productName}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                  />
                </Grid>

                {/* Normal Price */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Normal Price (₹) *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="priceNormal"
                    value={formData.priceNormal}
                    onChange={handleInputChange}
                    placeholder="Enter normal price"
                  />
                </Grid>

                {/* Discount Price */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Discount Price (₹) *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="discountPrice"
                    value={formData.discountPrice}
                    onChange={handleInputChange}
                    placeholder="Enter discount price"
                  />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <ArgonButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/product")}
                  disabled={loading}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                  {loading ? "Updating..." : "Update Product"}
                </ArgonButton>
              </ArgonBox>
            </form>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditProduct;
